/**
 * Modal de detalles de guías/ítems para Control Semanal
 */

import React from 'react';
import { Modal, Button, Table, Badge } from 'react-bootstrap';
import * as FaIcons from 'react-icons/fa';
import { formatDate } from '../utils/helpers';
import { STATE_COLORS, STATE_LABELS } from '../utils/constants';

const DetailModal = ({
  show,
  onHide,
  selectedCell,
  detailData,
  detailLoading,
  selectedGuias,
  onGuiaSelection,
  onSelectAllGuias,
  onOpenSubestadoModal,
  onStartReconciliation,
  onOpenExcelModal,
  currentExportParams,
  reconciliationLoading,
  subestadoLoading
}) => {
  if (!selectedCell) return null;

  const isOrphanView = selectedCell.state === 'Sin estado';
  const hasSelectedGuias = Object.values(selectedGuias).some(Boolean);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="detail-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex justify-content-between align-items-center w-100">
          <span>
            <FaIcons.FaList className="me-2" />
            Detalles: {formatDate(selectedCell.date)} -{' '}
            {selectedCell.state === 'Todas las guías'
              ? 'Todas las guías'
              : STATE_LABELS[selectedCell.state] || selectedCell.state}
            {` (${selectedCell.count} ${isOrphanView ? 'ítems' : 'guías'})`}
          </span>
          {/* Botón de exportación a Excel - No mostrar para "Sin estado" */}
          {!isOrphanView && currentExportParams && (
            <Button
              variant="success"
              size="sm"
              onClick={onOpenExcelModal}
              className="ms-3"
              title="Exportar a Excel"
            >
              <FaIcons.FaFileExcel className="me-1" />
              Exportar Excel
            </Button>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {detailLoading ? (
          <div className="text-center py-4">
            <FaIcons.FaSpinner className="fa-spin" size={30} />
            <p className="mt-3">Cargando detalles...</p>
          </div>
        ) : detailData.length > 0 ? (
          <DetailTable
            detailData={detailData}
            isOrphanView={isOrphanView}
            selectedGuias={selectedGuias}
            onGuiaSelection={onGuiaSelection}
          />
        ) : (
          <div className="text-center py-4">
            <FaIcons.FaInfoCircle size={40} className="text-muted mb-3" />
            <p className="text-muted">
              {isOrphanView
                ? 'No se encontraron ítems huérfanos para esta fecha'
                : 'No se encontraron guías para esta combinación de fecha y estado'}
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {isOrphanView && detailData.length > 0 && (
          <Button
            variant="primary"
            onClick={onStartReconciliation}
            disabled={reconciliationLoading}
            className="me-auto"
          >
            {reconciliationLoading ? (
              <>
                <FaIcons.FaSpinner className="fa-spin me-2" />
                Buscando matches...
              </>
            ) : (
              <>
                <FaIcons.FaLink className="me-2" />
                Reconciliar Ítems
              </>
            )}
          </Button>
        )}
        {!isOrphanView && detailData.length > 0 && (
          <>
            <div className="me-auto d-flex align-items-center">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => onSelectAllGuias(true)}
                className="me-2"
              >
                <FaIcons.FaCheckSquare className="me-1" />
                Todos
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => onSelectAllGuias(false)}
                className="me-2"
              >
                <FaIcons.FaSquare className="me-1" />
                Ninguno
              </Button>
            </div>
            {hasSelectedGuias && (
              <Button
                variant="success"
                onClick={onOpenSubestadoModal}
                disabled={subestadoLoading}
                className="me-2"
              >
                {subestadoLoading ? (
                  <>
                    <FaIcons.FaSpinner className="fa-spin me-2" />
                    Cargando...
                  </>
                ) : (
                  <>
                    <FaIcons.FaEdit className="me-2" />
                    Cambiar Subestado ({Object.values(selectedGuias).filter(Boolean).length})
                  </>
                )}
              </Button>
            )}
          </>
        )}
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Sub-componente de tabla de detalles
const DetailTable = ({ detailData, isOrphanView, selectedGuias, onGuiaSelection }) => {
  return (
    <div className="table-responsive">
      <Table striped bordered hover size="sm">
        <thead className="table-dark">
          <tr>
            {!isOrphanView && <th style={{ width: '50px' }}>Sel.</th>}
            <th>{isOrphanView ? 'N° Item' : 'N° Guía'}</th>
            <th>{isOrphanView ? 'Cliente' : 'Ruta'}</th>
            <th>Estado</th>
            <th>Sub Estado</th>
            <th>Fecha Creación</th>
            <th>{isOrphanView ? 'Fecha Recepción' : 'Items'}</th>
          </tr>
        </thead>
        <tbody>
          {detailData.map((item, index) => {
            const isOrphanItem = isOrphanView || item.tipo_item === 'orphan';
            const isSelected = selectedGuias[item.guia_id] || false;

            return (
              <tr key={item.guia_id || item.item_id || item.id || index}>
                {!isOrphanView && (
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) =>
                        onGuiaSelection(item.guia_id, e.target.checked)
                      }
                      disabled={!item.guia_id}
                    />
                  </td>
                )}
                <td className="fw-bold">
                  {isOrphanItem
                    ? item.item || item.item_cleaned || item.item_id
                    : item.guia || item.guia_id}
                </td>
                <td>
                  {isOrphanItem
                    ? item.cliente?.cliente || 'N/A'
                    : item.ruta_actual?.ruta || item.ruta_id || '-'}
                </td>
                <td>
                  <Badge
                    bg={
                      isOrphanItem
                        ? item.reception_info?.estado === 'Recepcionado'
                          ? 'info'
                          : 'secondary'
                        : STATE_COLORS[item.ultimo_estado?.estado] || 'secondary'
                    }
                  >
                    {isOrphanItem
                      ? item.reception_info?.estado || 'Sin Estado'
                      : STATE_LABELS[item.ultimo_estado?.estado] ||
                        item.ultimo_estado?.estado ||
                        'Sin Estado'}
                  </Badge>
                </td>
                <td>
                  {isOrphanItem
                    ? item.reception_info?.sub_estado || '-'
                    : item.subestado_info?.subestado ||
                      item.ultimo_estado?.sub_estado ||
                      '-'}
                </td>
                <td>
                  {item.created_date
                    ? new Date(item.created_date).toLocaleDateString('es-CL')
                    : '-'}
                </td>
                <td className="text-center">
                  {isOrphanItem
                    ? item.reception_info?.fecha_recepcion
                      ? (() => {
                          const date = new Date(
                            item.reception_info.fecha_recepcion + 'T12:00:00'
                          );
                          return date.toLocaleDateString('es-CL', {
                            timeZone: 'America/Santiago'
                          });
                        })()
                      : '-'
                    : item.total_items || 0}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default DetailModal;
